import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * Input primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.4
 *
 * Variants:
 *   - `default` — `border border-input bg-background shadow-none`.
 *     Use on standalone forms, settings pages, and dialogs.
 *   - `flat` — `border-0 bg-muted/50 hover:bg-muted`.
 *     **Mandatory inside `<SlideDrawer>` / `<FormDrawer>` edit forms** so the
 *     drawer reads as a single calm surface instead of a stack of bordered
 *     widgets.
 *
 * Metrics shared with Textarea and SelectTrigger so a row of mixed inputs
 * aligns:  `h-9`, `text-base md:text-sm` (16px on mobile prevents iOS Safari
 * auto-zoom on focus, then collapses to 14px from md+), `px-3 py-2`.
 */
const inputVariants = cva(
  [
    "flex h-9 w-full rounded-md px-3 py-2",
    "text-base md:text-sm",
    "ring-offset-background placeholder:text-muted-foreground",
    "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border border-input bg-background shadow-none",
        flat: "border-0 bg-muted/50 hover:bg-muted shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant }), className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input, inputVariants };
